type SummaryRule = {
  id: number | string;
  name: string;
  eval?: string | number | null;
  unit?: string | null;
  slug?: string | null;
  order?: number | null;
};

type SummaryGroup = {
  id: number | string;
  name: string;
  rules: SummaryRule[];
};

type SummarySegment = {
  id: number | string;
  name: string;
  rules: SummaryRule[];
  groups: SummaryGroup[];
};

type SummaryClient = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  street?: string | null;
  house_number?: string | null;
  apartment_number?: string | null;
  postal_code?: string | null;
  city?: string | null;
  province?: string | null;
};

export type CalculationSummaryCsvRow = {
  name: string;
  value: string;
  unit: string;
  section: string;
};

type BuildCalculationSummaryCsvRowsParams = {
  client?: SummaryClient | null;
  sidebarRules?: SummaryRule[] | null;
  widgetRules?: SummaryRule[] | null;
  regularSegments?: SummarySegment[] | null;
  financingSegments?: SummarySegment[] | null;
};

function createRow(
  name: string,
  value: string,
  unit: string,
  section: string
): CalculationSummaryCsvRow {
  return {
    name,
    value,
    unit,
    section,
  };
}

function getRuleValue(rule?: SummaryRule | null) {
  if (!rule || rule.eval == null || rule.eval === '') {
    return '-';
  }

  return String(rule.eval);
}

function getRuleUnit(rule?: SummaryRule | null) {
  return rule?.unit ? String(rule.unit) : '';
}

function getFlooredValue(rule?: SummaryRule | null) {
  if (!rule || rule.eval == null || rule.eval === '') {
    return '-';
  }

  const numericValue = Math.floor(Number(rule.eval));

  return Number.isFinite(numericValue) && numericValue > 0 ? String(numericValue) : '-';
}

function findRuleBySlug(segment: SummarySegment, slug: string) {
  return segment.rules.find((rule) => rule.slug === slug);
}

function findGroupRuleBySlug(segment: SummarySegment, slug: string) {
  return segment.groups.flatMap((group) => group.rules).find((rule) => rule.slug === slug);
}

function buildFinancingRows(segment: SummarySegment) {
  const sectionBase = `Podsumowanie ${segment.name}`;

  const netPrice = findRuleBySlug(segment, 'cena-netto-dla-klienta-wszystkie-technologie');
  const grossPrice = findRuleBySlug(segment, 'cena-brutto-dla-klienta-wszystkie-technologie');
  const subsidy = findRuleBySlug(segment, 'dotacja');
  const clientContribution = findRuleBySlug(segment, 'kwota-po-dotacji-wklad-wlasny-klienta');
  const thermalDeduction = findRuleBySlug(segment, 'ulga-termomodernizacyjna-do-odliczenia');
  const vatPayment = findRuleBySlug(segment, 'platnosc-1-vat-od-pierwszej-transzy-dotacji-klient');
  const firstSubsidy = findRuleBySlug(
    segment,
    'platnosc-2-pierwsza-transza-dotacji-35percent-wfos'
  );
  const secondSubsidy = findRuleBySlug(
    segment,
    'platnosc-3-druga-transza-dotacji-wfos-po-montazu-i-oplaceniu-faktury-koncowej'
  );
  const clientPaymentAfterInstallation = findRuleBySlug(
    segment,
    'platnosc-4-wklad-wlasny-klienta-klient-po-montazu'
  );
  const trifyInterestRate = findRuleBySlug(segment, 'wariant-2-rata-trify-odsetkowa');
  const externalFinancing = findRuleBySlug(segment, 'wariant-2-rata-finansowanie-zewnetrzne');
  const loanAmount = findGroupRuleBySlug(segment, 'kwota-kredytu-netto-do-wyplaty');
  const installmentCount = findGroupRuleBySlug(segment, 'liczba-rat');
  const interestRate = findGroupRuleBySlug(segment, 'oprocentowanie-nominalne-w-skali-roku');
  const finalClientCost = loanAmount || clientContribution;

  const financingRows: CalculationSummaryCsvRow[] = [
    createRow(
      netPrice?.name || 'Cena netto',
      getRuleValue(netPrice),
      getRuleUnit(netPrice),
      `${sectionBase} / Podsumowanie ceny`
    ),
    createRow(
      grossPrice?.name || 'Cena brutto',
      getRuleValue(grossPrice),
      getRuleUnit(grossPrice),
      `${sectionBase} / Podsumowanie ceny`
    ),
    createRow(
      subsidy?.name || 'Dotacja',
      getRuleValue(subsidy),
      getRuleUnit(subsidy),
      `${sectionBase} / Podsumowanie ceny`
    ),
    createRow(
      clientContribution?.name || 'Kwota po dotacji (wkład klienta)',
      getRuleValue(clientContribution),
      getRuleUnit(clientContribution),
      `${sectionBase} / Podsumowanie ceny`
    ),
    createRow(
      thermalDeduction?.name || 'Ulga termomodernizacyjna do odliczenia',
      getRuleValue(thermalDeduction),
      getRuleUnit(thermalDeduction),
      `${sectionBase} / Podsumowanie ceny`
    ),
    createRow(
      'VAT (od pierwszej transzy dotacji)',
      getRuleValue(vatPayment),
      getRuleUnit(vatPayment),
      `${sectionBase} / Proces płatności`
    ),
    createRow(
      'Pierwsza transza dotacji (35%)',
      getRuleValue(firstSubsidy),
      getRuleUnit(firstSubsidy),
      `${sectionBase} / Proces płatności`
    ),
    createRow(
      'Wkład własny klienta',
      getRuleValue(loanAmount || clientPaymentAfterInstallation),
      getRuleUnit(loanAmount || clientPaymentAfterInstallation),
      `${sectionBase} / Wkład własny klienta`
    ),
    createRow(
      'RRSO',
      getRuleValue(interestRate),
      getRuleUnit(interestRate),
      `${sectionBase} / Opcja 2`
    ),
    createRow('Liczba miesięcy', getFlooredValue(installmentCount), '', `${sectionBase} / Opcja 2`),
    createRow(
      'Rata miesięczna',
      getRuleValue(externalFinancing),
      getRuleUnit(externalFinancing),
      `${sectionBase} / Opcja 2`
    ),
    createRow(
      'Druga transza dotacji wypłacona przez WFOŚiGW po montażu i opłaceniu faktury końcowej',
      getRuleValue(secondSubsidy),
      getRuleUnit(secondSubsidy),
      `${sectionBase} / Druga transza dotacji`
    ),
    createRow(
      'Rata miesięczna',
      getRuleValue(trifyInterestRate),
      getRuleUnit(trifyInterestRate),
      `${sectionBase} / Opcja 2 Trify`
    ),
    createRow(
      'Do odliczenia ulga termomodernizacyjna w podatku dochodowym',
      getRuleValue(thermalDeduction),
      getRuleUnit(thermalDeduction),
      `${sectionBase} / Podsumowanie końcowe`
    ),
    createRow(
      'Cena finalna',
      getRuleValue(finalClientCost),
      getRuleUnit(finalClientCost),
      `${sectionBase} / Podsumowanie końcowe`
    ),
  ];

  return financingRows;
}

function buildClientRows(client: SummaryClient): CalculationSummaryCsvRow[] {
  const section = 'Dane klienta';

  const fullName = [client.first_name, client.last_name]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');

  const addressLine = [
    [client.street, client.house_number].filter(Boolean).join(' '),
    client.apartment_number ? `m. ${client.apartment_number}` : '',
  ]
    .filter(Boolean)
    .join(' / ');

  const cityLine = [client.postal_code, client.city]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');

  const fields: Array<[string, string | null | undefined]> = [
    ['Imię i nazwisko', fullName],
    ['E-mail', client.email],
    ['Telefon', client.phone],
    ['Adres', addressLine],
    ['Miejscowość', cityLine],
    ['Województwo', client.province],
  ];

  return fields
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([label, value]) => createRow(label, String(value), '', section));
}

export function buildCalculationSummaryCsvRows({
  client,
  sidebarRules,
  widgetRules,
  regularSegments,
  financingSegments,
}: BuildCalculationSummaryCsvRowsParams) {
  const rows: CalculationSummaryCsvRow[] = [];

  if (client) {
    rows.push(...buildClientRows(client));
  }

  sidebarRules?.forEach((rule) => {
    rows.push(
      createRow(
        rule.name,
        getRuleValue(rule),
        getRuleUnit(rule),
        'Podsumowanie kalkulacji / Panel boczny'
      )
    );
  });

  widgetRules?.forEach((rule) => {
    rows.push(
      createRow(rule.name, getRuleValue(rule), getRuleUnit(rule), 'Podsumowanie kalkulacji')
    );
  });

  financingSegments?.forEach((segment) => {
    rows.push(...buildFinancingRows(segment));
  });

  regularSegments?.forEach((segment) => {
    const segmentSection = `Podsumowanie ${segment.name}`;

    segment.rules.forEach((rule) => {
      rows.push(createRow(rule.name, getRuleValue(rule), getRuleUnit(rule), segmentSection));
    });

    segment.groups.forEach((group) => {
      const groupSection = `${segmentSection} / ${group.name}`;

      group.rules.forEach((rule) => {
        rows.push(createRow(rule.name, getRuleValue(rule), getRuleUnit(rule), groupSection));
      });
    });
  });

  return rows;
}
